import { createSlice } from '@reduxjs/toolkit'
import { useDispatch } from 'react-redux'

const getFilterCount = filters => {
    let filterCount = 0
    if (filters.assetIsDownFilter) {
        filterCount = filterCount + 1
    }
    if (Array.isArray(filters.typeFilters) && filters.typeFilters.length) {
        filterCount = filterCount + filters.typeFilters.length
    }
    if (
        Array.isArray(filters.locationFilters) &&
        filters.locationFilters.length
    ) {
        filterCount = filterCount + filters.locationFilters.length
    }

    if (Array.isArray(filters.statusFilters) && filters.statusFilters.length) {
        filterCount = filterCount + filters.statusFilters.length
    }
    return filterCount
}

const initialState = {
    locations: [],
    openRequests: [],
    closedRequests: [],
    estimateRequests: [],
    openPMRequests: [],
    closedPMRequests: [],
    make: '',
    model: '',
    requestFilters: {
        query: '',
        count: 0,
        filterApplied: false,
        locationFilters: [],
        typeFilters: [],
        statusFilters: [],
        assetIsDownFilter: false,
    },
    formIsLoading: true,
    formError: '',
    filters: [],
    pmsFilters: [],
    pastPmsFilters: [],
    estimateFilters: [],
    pastRequestsFilters: [],
    needsUpdateRequest: true,
    needsUpdateDashboard: true,
    needsUpdatePM: true,
}

const requestSlice = createSlice({
    name: 'Request',
    initialState,
    reducers: {
        saveRequestFilters(state, { payload }) {
            let newFilters = { ...payload.filters }
            const filterCount = getFilterCount(newFilters)
            newFilters.count = filterCount
            newFilters.filterApplied = filterCount > 0
            state.requestFilters = newFilters
        },
        resetAllFilters(state) {
            state.requestFilters = initialState.requestFilters
            state.pastRequestsFilters = initialState.pastRequestsFilters
            state.pastPmsFilters = initialState.pastPmsFilters
            state.estimateFilters = initialState.estimateFilters
            state.filters = initialState.filters
        },
        changeNeedsUpdate(state, { payload }) {
            state.needsUpdateRequest = payload.request
            state.needsUpdateDashboard = payload.dashboard
        },
        changeNeedsUpdatePM(state, { payload }) {
            state.needsUpdatePM = payload.plannedMaintenance
        },
        resetNeedsUpdate(state) {
            state.needsUpdateRequest = true
            state.needsUpdateDashboard = true
            state.needsUpdatePM = true
        },
        requestError(state, { payload }) {
            state.formIsLoading = false
            state.formError = payload
        },
        requestLoading(state, { payload }) {
            state.formIsLoading = payload
            state.formError = false
            state.formSuccess = false
        },
        requestSuccess(state) {
            state.formIsLoading = false
            state.formSuccess = true
        },
        clearAssetStore(state) {
            state.make = ''
            state.model = ''
        },
        storeAssetDetails(state, { payload }) {
            state.make = payload?.asset?.manufacturer
            state.model = payload?.asset?.model
        },
        storeLocations(state, { payload }) {
            state.formIsLoading = false
            state.locations = payload
        },
        storeRequestList(state, { payload }) {
            state.openRequests = payload?.openRequests
            state.closedRequests = payload?.closedRequests
            state.estimateRequests = payload?.estimateRequests
            state.formIsLoading = false
        },
        storePMRequestList(state, { payload }) {
            state.openPMRequests = payload?.openPMRequests
            state.closedPMRequests = payload?.closedPMRequests
            state.formIsLoading = false
        },
        saveFilters(state, { payload }) {
            state.filters = payload
        },
        savePMsFilters(state, { payload }) {
            state.pmsFilters = payload
        },
        savePastPmsFilters(state, { payload }) {
            state.pastPmsFilters = payload
        },
        saveEstimateFilters(state, { payload }) {
            state.estimateFilters = payload
        },
        savePastRequestsFilters(state, { payload }) {
            state.pastRequestsFilters = payload
        },
        clearRequestSlice(state) {
            state.openRequests = initialState?.openRequests
            state.closedRequests = initialState?.closedRequests
            state.openPMRequests = initialState?.openPMRequests
            state.closedPMRequests = initialState?.closedPMRequests
            state.filters = initialState?.filters
            state.pastRequestsFilters = initialState.pastRequestsFilters
            state.pastPmsFilters = initialState.pastPmsFilters
            state.pmsFilters = initialState.pmsFilters
            state.estimateFilters = initialState.estimateFilters
            state.formError = initialState?.formError
            state.formIsLoading = initialState?.formIsLoading
            state.locations = initialState?.locations
            state.make = initialState?.make
            state.model = initialState?.model
            state.requestFilters = { ...initialState.requestFilters }
        },
    },
})

export const { actions } = requestSlice

export default requestSlice.reducer

export const useRequestActions = () => {
    const dispatch = useDispatch()

    const saveRequestFilters = filters => {
        return dispatch(actions.saveRequestFilters({ filters }))
    }
    const resetAllFilters = () => {
        return dispatch(actions.resetAllFilters())
    }
    const requestError = error => {
        return dispatch(actions.requestError(error))
    }
    const requestLoading = loading => {
        return dispatch(actions.requestLoading(loading))
    }
    const requestSuccess = () => {
        return dispatch(actions.requestSuccess())
    }
    const clearAssetStore = () => {
        return dispatch(actions.clearAssetStore())
    }
    const storeAssetDetails = asset => {
        return dispatch(actions.storeAssetDetails({ asset }))
    }
    const storeLocations = locations => {
        return dispatch(actions.storeLocations(locations))
    }
    const storeRequestList = (
        openRequests,
        closedRequests,
        estimateRequests,
    ) => {
        return dispatch(
            actions.storeRequestList({
                openRequests,
                closedRequests,
                estimateRequests,
            }),
        )
    }
    const storePMRequestList = (openPMRequests, closedPMRequests) => {
        return dispatch(
            actions.storePMRequestList({
                openPMRequests,
                closedPMRequests,
            }),
        )
    }
    const saveFilters = filters => {
        return dispatch(actions.saveFilters(filters))
    }

    const savePmsFilters = filters => {
        return dispatch(actions.savePMsFilters(filters))
    }

    const savePastPmsFilters = filters => {
        return dispatch(actions.savePastPmsFilters(filters))
    }

    const saveEstimateFilters = filters => {
        return dispatch(actions.saveEstimateFilters(filters))
    }

    const savePastRequestsFilters = filters => {
        return dispatch(actions.savePastRequestsFilters(filters))
    }

    const clearRequests = () => {
        return dispatch(actions.clearRequestSlice())
    }

    const changeNeedsUpdate = needsUpdate => {
        return dispatch(actions.changeNeedsUpdate(needsUpdate))
    }

    const changeNeedsUpdatePM = needsUpdate => {
        return dispatch(actions.changeNeedsUpdatePM(needsUpdate))
    }

    const resetNeedsUpdate = () => {
        return dispatch(actions.resetNeedsUpdate())
    }

    return {
        saveRequestFilters,
        resetAllFilters,
        requestError,
        requestLoading,
        requestSuccess,
        clearAssetStore,
        storeAssetDetails,
        storeLocations,
        storeRequestList,
        storePMRequestList,
        saveFilters,
        clearRequests,
        savePmsFilters,
        savePastPmsFilters,
        saveEstimateFilters,
        savePastRequestsFilters,
        changeNeedsUpdate,
        changeNeedsUpdatePM,
        resetNeedsUpdate,
    }
}
